/**
 * React Router v7 route definitions.
 *
 * Wave A ships only the Placeholder page wired to every route. Wave B
 * and C will replace each `element` with the real page component.
 * Keeping the path strings here in one place means Wave B agents only
 * edit this file to swap implementations.
 */

import { useEffect, useRef } from 'react';
import {
  createBrowserRouter,
  Outlet,
  RouterProvider,
  useLocation,
  useNavigate,
} from 'react-router-dom';

import { AdminDisputeDetailPage } from '@/pages/AdminDisputeDetailPage';
import { AdminDisputeQueuePage } from '@/pages/AdminDisputeQueuePage';
import { DealDetailPage } from '@/pages/DealDetailPage';
import { DealListPage } from '@/pages/DealListPage';
import { DisputeFlowPage } from '@/pages/DisputeFlowPage';
import { InvitePage } from '@/pages/InvitePage';
import { NewDealWizardPage } from '@/pages/NewDealWizardPage';
import { OnboardingPage } from '@/pages/OnboardingPage';
import { Placeholder } from '@/pages/Placeholder';
import { ProfilePage } from '@/pages/ProfilePage';

/**
 * Read the Telegram Mini-App `start_param` once on mount. When the user
 * arrives via a `t.me/<bot>?startapp=invite_<token>` deep link, Telegram
 * sets this field and we route the user straight into the InvitePage
 * accept flow — before the normal onboarding redirect kicks in.
 *
 * We use a ref so the redirect fires exactly once; otherwise React
 * StrictMode double-invokes the effect and can trigger a second
 * navigate that interferes with user interactions.
 */
function InviteDeepLinkRedirect(): null {
  const navigate = useNavigate();
  const location = useLocation();
  const handledRef = useRef(false);

  useEffect(() => {
    if (handledRef.current) return;
    handledRef.current = true;

    const startParam =
      window.Telegram?.WebApp?.initDataUnsafe?.start_param ?? '';
    if (!startParam.startsWith('invite_')) return;

    const token = startParam.slice('invite_'.length);
    if (!/^[0-9a-f]{32}$/.test(token)) return;

    // Avoid redirecting if we're already on the invite page (e.g. because
    // the user landed there via an explicit URL).
    if (location.pathname.startsWith('/invite/')) return;

    navigate(`/invite/${token}`, { replace: true });
  }, [navigate, location.pathname]);

  return null;
}

export const ROUTES = {
  home: '/',
  deals: '/deals',
  newDeal: '/deals/new',
  dealDetail: '/deals/:shortId',
  dealDispute: '/deals/:shortId/dispute',
  invite: '/invite/:token',
  profile: '/profile',
  adminDisputes: '/admin/disputes',
  adminDisputeDetail: '/admin/disputes/:disputeId',
} as const;

/**
 * Layout wrapper that mounts the invite deep-link redirect once and
 * then renders the matched child route via an `<Outlet>`.
 */
function RootLayout(): JSX.Element {
  return (
    <>
      <InviteDeepLinkRedirect />
      <Outlet />
    </>
  );
}

const router = createBrowserRouter([
  {
    element: <RootLayout />,
    children: [
      { path: ROUTES.home, element: <OnboardingPage /> },
      { path: ROUTES.deals, element: <DealListPage /> },
      { path: ROUTES.newDeal, element: <NewDealWizardPage /> },
      { path: ROUTES.dealDetail, element: <DealDetailPage /> },
      { path: ROUTES.dealDispute, element: <DisputeFlowPage /> },
      { path: ROUTES.invite, element: <InvitePage /> },
      { path: ROUTES.profile, element: <ProfilePage /> },
      { path: ROUTES.adminDisputes, element: <AdminDisputeQueuePage /> },
      { path: ROUTES.adminDisputeDetail, element: <AdminDisputeDetailPage /> },
      { path: '*', element: <Placeholder pageName="Not Found" /> },
    ],
  },
]);

export function AppRouter(): JSX.Element {
  return <RouterProvider router={router} />;
}
